import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

/* -------------------------------------------------------
   Grid Plane shaders - perspective grid floor
   ------------------------------------------------------- */

const gridVertexShader = `
uniform float uTime;

varying vec2 vUv;
varying vec3 vWorldPos;
varying float vElevation;

// Simplex-style noise for terrain
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x * 34.0) + 1.0) * x); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439,
                      -0.577350269189626, 0.024390243902439);
  vec2 i  = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0))
                            + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy),
                           dot(x12.zw, x12.zw)), 0.0);
  m = m * m;
  m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

void main() {
  vUv = uv;

  vec3 pos = position;

  // Terrain elevation using layered noise (static)
  vec2 noiseCoord = pos.xy * 0.08;

  float elevation = 0.0;
  elevation += snoise(noiseCoord) * 2.5;
  elevation += snoise(noiseCoord * 2.5) * 0.8;
  elevation += snoise(noiseCoord * 6.0) * 0.2;

  // Flatness mask: large smooth zones that stay flat
  float flatMask = snoise(pos.xy * 0.025 + 50.0);   // offset to decorrelate from terrain
  flatMask = smoothstep(-0.2, 0.5, flatMask);         // 0 = flat, 1 = full elevation
  elevation *= flatMask;

  // Horizon mountains: large slow noise that ramps up with distance
  float distCenter = length(pos.xy);
  float horizonFactor = smoothstep(40.0, 120.0, distCenter);
  float mountains = snoise(pos.xy * 0.015 + 100.0) * 6.0
                  + snoise(pos.xy * 0.035 + 200.0) * 2.5;
  mountains = max(mountains, 0.0); // only peaks, no deep valleys
  elevation += mountains * horizonFactor;

  pos.z += elevation;
  vElevation = elevation;

  vec4 worldPos = modelMatrix * vec4(pos, 1.0);
  vWorldPos = worldPos.xyz;
  gl_Position = projectionMatrix * viewMatrix * worldPos;
}
`;

const gridFragmentShader = `
uniform float uTime;
uniform float uScrollProgress;
uniform float uVelocity;

varying vec2 vUv;
varying vec3 vWorldPos;
varying float vElevation;

// Simplex noise (duplicated for per-pixel elevation in fragment shader)
vec3 mod289f(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289f(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permutef(vec3 x) { return mod289f(((x * 34.0) + 1.0) * x); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439,
                      -0.577350269189626, 0.024390243902439);
  vec2 i  = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289f(i);
  vec3 p = permutef(permutef(i.y + vec3(0.0, i1.y, 1.0))
                            + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy),
                           dot(x12.zw, x12.zw)), 0.0);
  m = m * m;
  m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

// Per-pixel elevation matching vertex shader terrain exactly
float getElevation(vec2 worldXZ) {
  // Plane is rotated -PI/2 on X: local XY -> world X, -Z
  vec2 localXY = vec2(worldXZ.x, -worldXZ.y);
  vec2 noiseCoord = localXY * 0.08;

  float elev = 0.0;
  elev += snoise(noiseCoord) * 2.5;
  elev += snoise(noiseCoord * 2.5) * 0.8;
  elev += snoise(noiseCoord * 6.0) * 0.2;

  float flatMask = snoise(localXY * 0.025 + 50.0);
  flatMask = smoothstep(-0.2, 0.5, flatMask);
  elev *= flatMask;

  // Horizon mountains (must match grid vertex shader)
  float distCenter = length(localXY);
  float horizonFactor = smoothstep(40.0, 120.0, distCenter);
  float mountains = snoise(localXY * 0.015 + 100.0) * 6.0
                  + snoise(localXY * 0.035 + 200.0) * 2.5;
  mountains = max(mountains, 0.0);
  elev += mountains * horizonFactor;

  return elev;
}

float sampleGrid(vec2 coord, float size, float thickness) {
  vec2 g = abs(fract(coord / size - 0.5) - 0.5);
  return 1.0 - smoothstep(0.0, thickness, min(g.x, g.y));
}

// Contour line with screen-space anti-aliasing via fwidth
float contourLine(float elevation, float interval, float width) {
  float scaled = elevation / interval;
  float fw = fwidth(scaled) * width;
  float f = fract(scaled);
  float distToLine = min(f, 1.0 - f);
  return 1.0 - smoothstep(0.0, fw, distToLine);
}

void main() {
  // RGB offset driven by scroll velocity
  float shift = clamp(uVelocity * 0.45, 0.0, 1.5);
  vec2 offR = vec2(-shift, shift * 0.3);
  vec2 offB = vec2( shift, -shift * 0.3);

  // Main grid per RGB channel (thinner)
  float gR = sampleGrid(vWorldPos.xz + offR,       2.0, 0.009);
  float gG = sampleGrid(vWorldPos.xz,              2.0, 0.009);
  float gB = sampleGrid(vWorldPos.xz + offB,       2.0, 0.009);

  // Sub-grid per RGB channel (thinner)
  float sR = sampleGrid(vWorldPos.xz + offR * 0.5, 0.5, 0.0035);
  float sG = sampleGrid(vWorldPos.xz,              0.5, 0.0035);
  float sB = sampleGrid(vWorldPos.xz + offB * 0.5, 0.5, 0.0035);

  // Radial fade
  float dist = length(vWorldPos.xz) / 250.0;
  float fade = 1.0 - smoothstep(0.4, 1.0, dist);

  // Zone variation: use elevation + world position to create patches
  // of lower opacity across the terrain (0.4 to 1.0 range)
  float zoneNoise = sin(vWorldPos.x * 0.15) * cos(vWorldPos.z * 0.12)
                  + sin(vWorldPos.x * 0.07 + 1.5) * cos(vWorldPos.z * 0.09 + 2.0);
  zoneNoise = zoneNoise * 0.25 + 0.5; // remap to ~0.0 - 1.0
  float elevFactor = smoothstep(-2.0, 2.0, vElevation) * 0.3 + 0.7; // valleys dimmer
  float zoneOpacity = clamp(zoneNoise * elevFactor, 0.35, 1.0);

  // Per-pixel elevation for smooth contour lines (not interpolated from vertices)
  float pixelElev = getElevation(vWorldPos.xz);

  // Contour lines with per-channel elevation offset (chromatic aberration)
  // Max terrain elevation is ~2.5-2.8; interval 0.65 gives 3 visible lines
  float elevShift = shift * 0.08;
  float cInterval = 0.65;

  float cR = contourLine(pixelElev + elevShift, cInterval, 2.5);
  float cG = contourLine(pixelElev,             cInterval, 2.5);
  float cB = contourLine(pixelElev - elevShift, cInterval, 2.5);

  // Hide contours in flat zones (low elevation) so only relief shows lines
  float contourFade = smoothstep(0.3, 0.6, abs(pixelElev));

  // Mask out contours where grid lines are (avoids bright intersection dots)
  float gridMask = max(max(gR, gG), max(gB, max(max(sR, sG), sB)));
  float contourMask = 1.0 - smoothstep(0.0, 0.5, gridMask);

  // Per-channel intensity (base 0.40 stays, modulated by zone)
  float r = (gR * 0.80 + sR * 0.25) * fade * zoneOpacity;
  float g = (gG * 0.80 + sG * 0.25) * fade * zoneOpacity;
  float b = (gB * 0.80 + sB * 0.25) * fade * zoneOpacity;

  // Monochrome contour brightness by elevation level
  // Low = dim gray, Mid = soft white, High = bright white
  float elevNorm = clamp(pixelElev / 8.0, 0.0, 1.0);
  float contourBrightness = 0.3 + elevNorm * 0.7; // 0.3 at low, 1.0 at peaks

  // Blend contour lines monochromatically, masked at grid intersections
  float contourIntensity = fade * zoneOpacity * contourFade * contourMask * contourBrightness;
  r += cR * 0.55 * contourIntensity;
  g += cG * 0.55 * contourIntensity;
  b += cB * 0.55 * contourIntensity;

  // Pulse & scroll boost
  float pulse = 0.92 + 0.08 * sin(uTime * 0.5);
  float boost = 1.0 + uScrollProgress * 0.3;
  r *= pulse * boost;
  g *= pulse * boost;
  b *= pulse * boost;

  // Tint split channels when moving
  float tint = smoothstep(0.0, 0.3, shift);
  r += tint * gR * 0.12 * fade;
  b += tint * gB * 0.12 * fade;

  float alpha = max(max(r, g), b);
  gl_FragColor = vec4(r, g, b, alpha);
}
`;

/* -------------------------------------------------------
   Sky gradient shaders - monochrome horizon in 3D background
   ------------------------------------------------------- */
const skyVertexShader = `
varying vec3 vWorldPos;

void main() {
  vec4 worldPos = modelMatrix * vec4(position, 1.0);
  vWorldPos = worldPos.xyz;
  gl_Position = projectionMatrix * viewMatrix * worldPos;
}
`;

const skyFragmentShader = `
uniform float uBaseTop;
uniform float uGlowStrength;
uniform float uMaxLuminance;
uniform float uHorizonCenter;
uniform float uHorizonWidth;
uniform float uLowerFadeStart;
uniform float uLowerFadeEnd;

varying vec3 vWorldPos;

void main() {
  vec3 viewDir = normalize(vWorldPos - cameraPosition);
  float y = viewDir.y;

  // Sky tone (top brighter, lower hemisphere stays dark)
  float sky = smoothstep(-0.12, 0.72, y);
  float lowerFade = smoothstep(uLowerFadeStart, uLowerFadeEnd, y);

  // Monochrome horizon band near terrain flattening line
  float horizonBand = exp(-pow((y - uHorizonCenter) / uHorizonWidth, 2.0));

  float base = mix(0.0, uBaseTop, sky);
  float glow = horizonBand * uGlowStrength;
  float luminance = clamp((base + glow) * lowerFade, 0.0, uMaxLuminance);

  gl_FragColor = vec4(vec3(luminance), 1.0);
}
`;

const cometVertexShader = `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const cometFragmentShader = `
uniform float uOpacity;
uniform float uSoftness;

varying vec2 vUv;

void main() {
  float x = clamp(vUv.x, 0.0, 1.0);
  float coreLine = pow(x, 2.9);
  float width = exp(-pow((vUv.y - 0.5) / max(uSoftness, 0.001), 2.0));

  float head = exp(
    -pow((x - 0.96) / 0.05, 2.0)
    -pow((vUv.y - 0.5) / (uSoftness * 0.9), 2.0)
  );

  float sparkle = exp(
    -pow((x - 0.995) / 0.012, 2.0)
    -pow((vUv.y - 0.5) / 0.07, 2.0)
  );

  float alpha = clamp(coreLine * width * 0.85 + head * 0.5 + sparkle, 0.0, 1.0) * uOpacity;
  float luminance = mix(0.68, 1.0, pow(x, 1.5));

  gl_FragColor = vec4(vec3(luminance), alpha);
}
`;

/* -------------------------------------------------------
   Inline shaders - B&W monochrome palette
   ------------------------------------------------------- */

const vertexShader = `
uniform float uTime;
uniform float uScrollProgress;
uniform float uPixelRatio;

attribute float aScale;
attribute float aSpeed;

varying vec3 vColor;
varying float vAlpha;

// Same simplex noise as terrain so particles know ground height
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x * 34.0) + 1.0) * x); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439,
                      -0.577350269189626, 0.024390243902439);
  vec2 i  = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0))
                            + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy),
                           dot(x12.zw, x12.zw)), 0.0);
  m = m * m;
  m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

// Compute terrain height at a world XZ position (must match grid shader exactly)
float getTerrainHeight(vec2 worldXZ) {
  vec2 noiseCoord = worldXZ * 0.08;
  float elevation = 0.0;
  elevation += snoise(noiseCoord) * 2.5;
  elevation += snoise(noiseCoord * 2.5) * 0.8;
  elevation += snoise(noiseCoord * 6.0) * 0.2;

  float flatMask = snoise(worldXZ * 0.025 + 50.0);
  flatMask = smoothstep(-0.2, 0.5, flatMask);
  elevation *= flatMask;

  // Horizon mountains (must match grid vertex shader)
  float distCenter = length(worldXZ);
  float horizonFactor = smoothstep(40.0, 120.0, distCenter);
  float mountains = snoise(worldXZ * 0.015 + 100.0) * 6.0
                  + snoise(worldXZ * 0.035 + 200.0) * 2.5;
  mountains = max(mountains, 0.0);
  elevation += mountains * horizonFactor;

  return -6.0 + elevation;  // terrain base Y is -6
}

void main() {
  vec3 pos = position;

  // Autonomous left-to-right flow with wrap so particles continuously cross the scene
  float t = uTime;
  float flowRange = 32.0;
  float flowSpeed = 2.0 + aSpeed * 1.8;
  pos.x = mod(pos.x + t * flowSpeed + flowRange, flowRange * 2.0) - flowRange;

  float phase = t * (0.9 + aSpeed * 0.7) + position.x * 0.18 + position.z * 0.14;
  pos.z += sin(phase) * 1.35 + cos(phase * 0.63) * 0.45;
  pos.y += sin(phase * 1.2 + position.z * 0.1) * 0.95
         + cos(t * (0.55 + aSpeed * 0.45) + position.x * 0.2) * 0.45;

  // Terrain uses local coords (x, y) where localY = -worldZ due to -PI/2 rotation
  float terrainY = getTerrainHeight(vec2(pos.x, -pos.z));
  float minHeight = terrainY + 0.3 + aScale * 0.5;
  pos.y = max(pos.y, minHeight);

  vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
  gl_Position = projectionMatrix * mvPosition;

  float size = aScale * uPixelRatio * 4.8;
  size *= (1.0 / -mvPosition.z);
  size = max(size, 1.9);
  gl_PointSize = size;

  // Brightness based on height above terrain
  float heightAboveTerrain = pos.y - terrainY;
  float brightness = 0.5 + heightAboveTerrain * 0.1 + sin(t * 0.45 + aSpeed) * 0.04;
  brightness = clamp(brightness, 0.35, 1.0);
  vColor = vec3(brightness);

  // Fade out with distance (proper smoothstep edge order)
  float dist = length(pos.xz) / 40.0;
  vAlpha = (1.0 - smoothstep(0.3, 1.0, dist)) * (0.4 + aScale * 0.6);
}
`;

const fragmentShader = `
varying vec3 vColor;
varying float vAlpha;

void main() {
  float dist = length(gl_PointCoord - vec2(0.5));
  if (dist > 0.5) discard;

  float alpha = 1.0 - smoothstep(0.0, 0.5, dist);
  alpha *= vAlpha;

  float glow = exp(-dist * 4.0) * 0.5;

  gl_FragColor = vec4(vColor, alpha + glow);
}
`;

/* -------------------------------------------------------
   Connection Lines between nearby particles
   ------------------------------------------------------- */
// eslint-disable-next-line no-unused-vars
function ConnectionLines({ positions, count, scrollProgress }) {
  const lineRef = useRef();
  const maxConnections = 600;
  const connectionDistance = 3.5;

  const linePositions = useMemo(() => new Float32Array(maxConnections * 6), []);
  const lineColors = useMemo(() => new Float32Array(maxConnections * 6), []);

  useFrame(() => {
    if (!lineRef.current) return;

    let idx = 0;
    const posArr = positions;
    const step = Math.max(1, Math.floor(count / 300));

    for (let i = 0; i < count && idx < maxConnections; i += step) {
      const ix = i * 3, iy = i * 3 + 1, iz = i * 3 + 2;
      for (let j = i + step; j < count && idx < maxConnections; j += step) {
        const jx = j * 3, jy = j * 3 + 1, jz = j * 3 + 2;
        const dx = posArr[ix] - posArr[jx];
        const dy = posArr[iy] - posArr[jy];
        const dz = posArr[iz] - posArr[jz];
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

        if (dist < connectionDistance) {
          const alpha = (1 - dist / connectionDistance) * 0.15;
          const o = idx * 6;
          linePositions[o] = posArr[ix];
          linePositions[o + 1] = posArr[iy];
          linePositions[o + 2] = posArr[iz];
          linePositions[o + 3] = posArr[jx];
          linePositions[o + 4] = posArr[jy];
          linePositions[o + 5] = posArr[jz];

          // Monochrome white/gray
          const brightness = 0.6 + scrollProgress.current * 0.2;
          lineColors[o] = brightness * alpha / 0.15;
          lineColors[o + 1] = brightness * alpha / 0.15;
          lineColors[o + 2] = brightness * alpha / 0.15;
          lineColors[o + 3] = brightness * alpha / 0.15;
          lineColors[o + 4] = brightness * alpha / 0.15;
          lineColors[o + 5] = brightness * alpha / 0.15;
          idx++;
        }
      }
    }

    for (let k = idx * 6; k < maxConnections * 6; k++) {
      linePositions[k] = 0;
      lineColors[k] = 0;
    }

    lineRef.current.geometry.attributes.position.needsUpdate = true;
    lineRef.current.geometry.attributes.color.needsUpdate = true;
    lineRef.current.geometry.setDrawRange(0, idx * 2);
  });

  return (
    <lineSegments ref={lineRef} frustumCulled={false}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={maxConnections * 2}
          array={linePositions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={maxConnections * 2}
          array={lineColors}
          itemSize={3}
        />
      </bufferGeometry>
      <lineBasicMaterial
        vertexColors
        transparent
        opacity={0.3}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </lineSegments>
  );
}

/* -------------------------------------------------------
   Particles
   ------------------------------------------------------- */
function Particles({ count }) {
  const pointsRef = useRef();

  const { positions, scales, speeds } = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const scales = new Float32Array(count);
    const speeds = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      // Spread across terrain area (XZ), concentrated near center
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.pow(Math.random(), 0.7) * 25; // concentrated near center, max 25 units
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;

      // Start Y well above terrain
      const y = -1.0 + Math.random() * 6.0; // -1 to 5, floating above terrain

      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;

      scales[i] = 0.5 + Math.random() * 1.5;
      speeds[i] = 0.15 + Math.random() * 0.6;
    }
    return { positions, scales, speeds };
  }, [count]);

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uScrollProgress: { value: 0 },
      uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
    }),
    []
  );

  useFrame((state) => {
    if (!pointsRef.current) return;
    const mat = pointsRef.current.material;
    mat.uniforms.uTime.value = state.clock.elapsedTime;
  });

  return (
    <points ref={pointsRef} frustumCulled={false}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
        <bufferAttribute attach="attributes-aScale" count={count} array={scales} itemSize={1} />
        <bufferAttribute attach="attributes-aSpeed" count={count} array={speeds} itemSize={1} />
      </bufferGeometry>
      <shaderMaterial
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

/* -------------------------------------------------------
   Grid Plane - perspective wireframe floor
   ------------------------------------------------------- */
function GridPlane({ scrollProgress, scrollVelocity }) {
  const meshRef = useRef();

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uScrollProgress: { value: 0 },
      uVelocity: { value: 0 },
    }),
    []
  );

  useFrame((state) => {
    if (!meshRef.current) return;
    const mat = meshRef.current.material;
    mat.uniforms.uTime.value = state.clock.elapsedTime;
    mat.uniforms.uScrollProgress.value = scrollProgress.current;
    mat.uniforms.uVelocity.value = scrollVelocity.current;
  });

  return (
    <mesh
      ref={meshRef}
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, -6, 0]}
      frustumCulled={false}
    >
      <planeGeometry args={[600, 600, 300, 300]} />
      <shaderMaterial
        vertexShader={gridVertexShader}
        fragmentShader={gridFragmentShader}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        side={THREE.DoubleSide}
        blending={THREE.AdditiveBlending}
      />
    </mesh>
  );
}

/* -------------------------------------------------------
   Sky gradient dome
   ------------------------------------------------------- */
function SkyGradient({ isMobile }) {
  const uniforms = useMemo(
    () => ({
      uBaseTop: { value: isMobile ? 0.08 : 0.05 },
      uGlowStrength: { value: isMobile ? 0.08 : 0.05 },
      uMaxLuminance: { value: isMobile ? 0.12 : 0.09 },
      uHorizonCenter: { value: isMobile ? 0.03 : 0.05 },
      uHorizonWidth: { value: isMobile ? 0.22 : 0.16 },
      uLowerFadeStart: { value: isMobile ? -0.7 : -0.55 },
      uLowerFadeEnd: { value: isMobile ? -0.05 : -0.12 },
    }),
    [isMobile]
  );

  return (
    <mesh frustumCulled={false} renderOrder={-10}>
      <sphereGeometry args={[420, 48, 32]} />
      <shaderMaterial
        vertexShader={skyVertexShader}
        fragmentShader={skyFragmentShader}
        uniforms={uniforms}
        side={THREE.BackSide}
        depthWrite={false}
        toneMapped={false}
      />
    </mesh>
  );
}

/* -------------------------------------------------------
   Comet - autonomous horizon pass
   ------------------------------------------------------- */
function Comet() {
  const cometRef = useRef();
  const headMatRef = useRef();
  const prevPosRef = useRef({ x: -72.0, y: 0.0 });

  const coreUniforms = useMemo(
    () => ({
      uOpacity: { value: 0.0 },
      uSoftness: { value: 0.11 },
    }),
    []
  );

  const glowUniforms = useMemo(
    () => ({
      uOpacity: { value: 0.0 },
      uSoftness: { value: 0.24 },
    }),
    []
  );

  const passRef = useRef({
    cycle: -1,
    duration: 3.6,
    startY: -0.45,
    endY: 0.35,
    z: -56.0,
    arc: 2.6,
  });

  useFrame((state) => {
    if (!cometRef.current) return;

    const cycleLength = 10.5;
    const now = state.clock.elapsedTime;
    const cycle = Math.floor(now / cycleLength);

    if (cycle !== passRef.current.cycle) {
      passRef.current.cycle = cycle;
      passRef.current.duration = THREE.MathUtils.randFloat(3.1, 4.2);
      passRef.current.startY = THREE.MathUtils.randFloat(-0.8, -0.3);
      passRef.current.endY = THREE.MathUtils.randFloat(0.2, 0.65);
      passRef.current.z = THREE.MathUtils.randFloat(-62.0, -52.0);
      passRef.current.arc = THREE.MathUtils.randFloat(2.2, 3.2);
      prevPosRef.current = { x: -92.0, y: passRef.current.startY };
    }

    const localTime = now - cycle * cycleLength;
    if (localTime > passRef.current.duration) {
      cometRef.current.visible = false;
      coreUniforms.uOpacity.value = 0.0;
      glowUniforms.uOpacity.value = 0.0;
      if (headMatRef.current) headMatRef.current.opacity = 0.0;
      return;
    }

    const p = localTime / passRef.current.duration;
    const easedP = THREE.MathUtils.smootherstep(p, 0.0, 1.0);
    const x = THREE.MathUtils.lerp(-92.0, 92.0, easedP);
    const yLinear = THREE.MathUtils.lerp(passRef.current.startY, passRef.current.endY, easedP);
    const curve = Math.sin(easedP * Math.PI);
    const y = yLinear + Math.pow(curve, 1.05) * passRef.current.arc;
    const z = passRef.current.z + Math.sin(easedP * Math.PI * 1.6) * 0.08;

    const fadeIn = THREE.MathUtils.smoothstep(p, 0.0, 0.12);
    const fadeOut = 1.0 - THREE.MathUtils.smoothstep(p, 0.78, 1.0);
    const alpha = fadeIn * fadeOut;

    const vx = x - prevPosRef.current.x;
    const vy = y - prevPosRef.current.y;
    const angle = Math.atan2(vy, Math.max(vx, 0.0001));
    cometRef.current.visible = true;
    cometRef.current.position.set(x, y, z);
    cometRef.current.rotation.set(0, 0, angle);

    coreUniforms.uOpacity.value = 0.78 * alpha;
    glowUniforms.uOpacity.value = 0.28 * alpha;
    if (headMatRef.current) headMatRef.current.opacity = 0.3 * alpha;
    prevPosRef.current = { x, y };
  });

  return (
    <group ref={cometRef} visible={false}>
      <mesh position={[-8.0, 0, 0]} renderOrder={2}>
        <planeGeometry args={[16.0, 1.2]} />
        <shaderMaterial
          vertexShader={cometVertexShader}
          fragmentShader={cometFragmentShader}
          uniforms={glowUniforms}
          transparent
          depthWrite={false}
          toneMapped={false}
          side={THREE.DoubleSide}
          blending={THREE.AdditiveBlending}
        />
      </mesh>

      <mesh position={[-6.8, 0, 0]} renderOrder={3}>
        <planeGeometry args={[13.0, 0.55]} />
        <shaderMaterial
          vertexShader={cometVertexShader}
          fragmentShader={cometFragmentShader}
          uniforms={coreUniforms}
          transparent
          depthWrite={false}
          toneMapped={false}
          side={THREE.DoubleSide}
          blending={THREE.AdditiveBlending}
        />
      </mesh>

      <mesh renderOrder={4}>
        <sphereGeometry args={[0.16, 18, 18]} />
        <meshBasicMaterial
          ref={headMatRef}
          color="#ffffff"
          transparent
          opacity={0.0}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>
    </group>
  );
}

/* -------------------------------------------------------
   Camera controller driven by scroll
   ------------------------------------------------------- */
function CameraController({ scrollProgress, mouse, heroSlide }) {
  const { camera } = useThree();

  useFrame(() => {
    const p = scrollProgress.current;
    const mx = mouse.current.x;
    const my = mouse.current.y;

    // Lateral offset driven by hero panel navigation
    const lateralOffset = (heroSlide ? heroSlide.current : 0) * 6;

    // Base orbit from scroll
    const angle = p * Math.PI * 0.6;
    const radius = 18 - p * 4;
    const height = 5 - p * 8;

    // Mouse adds vertical + horizontal offset + hero lateral pan
    camera.position.x = Math.sin(angle) * radius + mx * 3 + lateralOffset;
    camera.position.y = height - my * 4;
    camera.position.z = Math.cos(angle) * radius;

    // Look target shifts with mouse parallax + hero lateral pan
    camera.lookAt(mx * 1.5 + lateralOffset, -my * 1.5, 0);
  });

  return null;
}

/* -------------------------------------------------------
   Scene
   ------------------------------------------------------- */
function Scene({ scrollProgress, scrollVelocity, mouse, heroSlide, isMobile }) {
  const particleCount = 10;

  return (
    <>
      <CameraController scrollProgress={scrollProgress} mouse={mouse} heroSlide={heroSlide} />
      <SkyGradient isMobile={isMobile} />
      <Comet />
      <ambientLight intensity={0.1} />
      <GridPlane scrollProgress={scrollProgress} scrollVelocity={scrollVelocity} />
      <Particles count={particleCount} />
    </>
  );
}

/* -------------------------------------------------------
   Exported Canvas wrapper
   ------------------------------------------------------- */
export default function ParticleNetwork({ scrollProgress, scrollVelocity, mouse, heroSlide }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div className="canvas-container">
      <Canvas
        camera={{ position: [0, 5, 18], fov: 60, near: 0.1, far: 800 }}
        dpr={[1, isMobile ? 1.5 : 2]}
        gl={{
          antialias: true,
          alpha: true,
          powerPreference: 'high-performance',
        }}
        style={{ background: 'transparent' }}
      >
        <Scene scrollProgress={scrollProgress} scrollVelocity={scrollVelocity} mouse={mouse} heroSlide={heroSlide} isMobile={isMobile} />
      </Canvas>
    </div>
  );
}
